import React, { useState, useRef, useEffect } from 'react';
import Calculator from './Calculator';

interface DraggableCalculatorModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const DraggableCalculatorModal: React.FC<DraggableCalculatorModalProps> = ({ isOpen, onClose }) => {
  const [position, setPosition] = useState({ x: 100, y: 100 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [isFocused, setIsFocused] = useState(true);
  const modalRef = useRef<HTMLDivElement>(null);

  // Initialiser la position au centre de l'écran au premier rendu
  useEffect(() => {
    if (isOpen && modalRef.current) {
      // Essayer de récupérer la position sauvegardée
      const savedPosition = localStorage.getItem('calculatorPosition');

      if (savedPosition) {
        try {
          const parsed = JSON.parse(savedPosition);
          // Vérifier que la position est toujours valide (dans les limites de l'écran)
          const maxX = window.innerWidth - (modalRef.current?.offsetWidth || 320);
          const maxY = window.innerHeight - (modalRef.current?.offsetHeight || 400);

          setPosition({
            x: Math.max(0, Math.min(parsed.x, maxX)),
            y: Math.max(0, Math.min(parsed.y, maxY)),
          });
        } catch (e) {
          // Si erreur de parsing, utiliser la position par défaut
          const rect = modalRef.current.getBoundingClientRect();
          setPosition({
            x: (window.innerWidth - rect.width) / 2,
            y: Math.max(50, (window.innerHeight - rect.height) / 2 - 100),
          });
        }
      } else {
        // Position par défaut au centre
        const rect = modalRef.current.getBoundingClientRect();
        setPosition({
          x: (window.innerWidth - rect.width) / 2,
          y: Math.max(50, (window.innerHeight - rect.height) / 2 - 100),
        });
      }

      setIsFocused(true); // Focus automatique à l'ouverture
    }
  }, [isOpen]);

  // Gérer les clics à l'extérieur pour retirer le focus
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(e.target as Node)) {
        setIsFocused(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleMouseDown = (e: React.MouseEvent) => {
    // Vérifier si on clique sur le header (pour le drag)
    const target = e.target as HTMLElement;
    const header = modalRef.current?.querySelector('.calculator-header');

    if (header && header.contains(target)) {
      setIsDragging(true);
      setDragOffset({
        x: e.clientX - position.x,
        y: e.clientY - position.y,
      });
    }
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (isDragging) {
      const newX = e.clientX - dragOffset.x;
      const newY = e.clientY - dragOffset.y;

      // Limiter la position pour ne pas sortir de l'écran
      const maxX = window.innerWidth - (modalRef.current?.offsetWidth || 0);
      const maxY = window.innerHeight - (modalRef.current?.offsetHeight || 0);

      const newPosition = {
        x: Math.max(0, Math.min(newX, maxX)),
        y: Math.max(0, Math.min(newY, maxY)),
      };

      setPosition(newPosition);
      // Sauvegarder la position dans localStorage
      localStorage.setItem('calculatorPosition', JSON.stringify(newPosition));
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, dragOffset]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] pointer-events-none" style={{ position: 'fixed' }}>
      <div
        ref={modalRef}
        className="absolute pointer-events-auto"
        style={{
          left: `${position.x}px`,
          top: `${position.y}px`,
          cursor: isDragging ? 'grabbing' : 'default',
        }}
        onMouseDown={handleMouseDown}
      >
        <div className="calculator-header">
          <Calculator onClose={onClose} isFocused={isFocused} onFocus={() => setIsFocused(true)} />
        </div>
      </div>
    </div>
  );
};

export default DraggableCalculatorModal;
